import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAppContext } from "../../contexts/AppContext";
import { Flex, Box, Image, IconButton, Tooltip, Link, Badge } from "@chakra-ui/react";
import {
  ArrowLeftToLine,
  ArrowRightToLine,
  Settings,
  Power,
  CircleGauge,
  BriefcaseBusiness,
  Activity,
  LineChart,
  ClipboardCheck,
} from "lucide-react";
import logo from "../../assets/img/logo.svg";
import { toast } from "react-toastify";

const Navigation = ({ setIsAuthenticated }) => {
  const { isCollapsed, setIsCollapsed, remainingTasks } = useAppContext();

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);
  useEffect(() => {
    localStorage.setItem("isCollapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1281 && !isCollapsed) {
        setIsCollapsed(true);
      } else if (window.innerWidth >= 1281 && isCollapsed) {
        setIsCollapsed(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed, setIsCollapsed]);

  const navigate = useNavigate();

  const handleLogout = () => {
    setIsAuthenticated(false);
    navigate("/login");
    toast.success({
      title: "You have been logged out.",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  return (
   
    <Flex
      as={"nav"}
      direction="column"
      w={isCollapsed ? "78px" : "220px"}
      minW={isCollapsed ? "78px" : "220px"}
      h="100dvh"
      py={8}
      px={4}
      justifyContent={"space-between"}
      alignItems={"center"}
      bg="grey.950"
      position="sticky"
      top="0"
      transition="all 0.35s ease"
      sx={{
        ".navLink": {
          textDecoration: "none !important",
          fontWeight: "500",
          display: "flex",
          flexDirection: "row !important",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: "var(--chakra-space-2) var(--chakra-space-4)",
          minHeight: "46.5px",
          color: "grey.300",
          borderRadius: 8,
          _hover: {
            color: "white",
            bg: "rgba(255,255,255,0.05)",
          },
        },
        ".navLink.active": {
          color: "white",
          bg: "brand.700",
        },
        ".navLink.active .lucide": {
          color: "white",
        },
      }}
    >
      <Flex
        direction="column"
        gap={{ lg: 24, base: 8 }}
        w="100%"
        justifyContent="center"
        alignItems="center"
        
      >
        <Flex
          direction="row"
          justifyContent={isCollapsed ? "center" : "space-between"}
          alignItems={"center"}
          minHeight="52px"
          w="100%"
          pr={4}
          pl={4}
        >
          {!isCollapsed && (
            <NavLink to={`/`}>
              <Image
                src={logo}
                w="72px"
                transition="all .2s ease"
                _hover={{
                  transform: "scale(.95)",
                }}
              />
            </NavLink>
          )}

          <IconButton
            size="sm"
            bg={"rgba(255,255,255,0.05)"}
            _hover={{ bg: "rgba(255,255,255,.1)" }}
            aria-label="Toggle Sidebar"
            icon={
              isCollapsed ? (
                <ArrowRightToLine size={14} />
              ) : (
                <ArrowLeftToLine size={14} />
              )
            }
            onClick={toggleCollapse}
          />
        </Flex>

        <Flex direction={"column"} justifyContent="flex-start" w="100%" gap={1}>
          <Tooltip
            label="Dashboard"
            isDisabled={!isCollapsed}
            placement="right"
          >
            <Link
              as={NavLink}
              to={`/`}
              pr={isCollapsed ? 0 : 8}
              className="navLink"
            >
              <Flex direction="row" alignItems="center" gap={2}>
                <CircleGauge size={16} />
                <Box
                  opacity={isCollapsed ? "0" : "1"}
                  transition="all 0.24s ease"
                >
                  Dashboard
                </Box>
              </Flex>
            </Link>
          </Tooltip>

        
          <Tooltip label="Funds" isDisabled={!isCollapsed} placement="right">
            <Link
              as={NavLink}
              to={`/funds/active`}
              pr={isCollapsed ? 0 : 8}
              className="navLink"
            >
              <Flex direction="row" alignItems="center" gap={2}>
                <BriefcaseBusiness size={16} />
                <Box
                  opacity={isCollapsed ? "0" : "1"}
                  transition="all 0.24s ease"
                >
                  Funds
                </Box>
              </Flex>
            </Link>
          </Tooltip>


          <Tooltip label="Tasks" isDisabled={!isCollapsed} placement="right">
            <Link
              as={NavLink}
              to={`/tasks/pending`}
              pr={isCollapsed ? 0 : 8}
              className="navLink"
            >
              {isCollapsed && remainingTasks > 0 && (
                    <Box
                      as="span"
                      justifyContent="center"
                      opacity={isCollapsed ? 1 : 0}
                      bg={"error.500"}
                      transitionDelay="1s"
                      border="2px solid #fff"
                      borderRadius="full"
                      height="9px"
                      width="9px"
                      position="absolute"
                      right="12px"
                      top="12px"
                    
                      z-index="1"></Box>
                )}
              <Flex direction="row" alignItems="center" gap={2}>
                <ClipboardCheck size={16} />
                <Box
                  opacity={isCollapsed ? "0" : "1"}
                  transition="all 0.24s ease"
                >
                  Tasks
                </Box>
              </Flex>

              {!isCollapsed && remainingTasks > 0 && (
                    <Badge colorScheme="error" variant="solid" >
                      {remainingTasks}
                    </Badge>
                  )}
            </Link>
          </Tooltip>


          <Tooltip label="Monitoring" isDisabled={!isCollapsed} placement="right">
            <Link
              as={NavLink}
              to={`/monitoring/documents`}
              pr={isCollapsed ? 0 : 8}
              className="navLink"
            >
              {isCollapsed && (
                    <Box
                      as="span"
                      justifyContent="center"
                      opacity={isCollapsed ? 1 : 0}
                      bg={"error.500"}
                      transitionDelay="1s"
                      border="2px solid #fff"
                      borderRadius="full"
                      height="9px"
                      width="9px"
                      position="absolute"
                      right="12px"
                      top="12px"
                    
                      z-index="1"></Box>
                )}
              <Flex direction="row" alignItems="center" gap={2}>
                <Activity size={16} />
                <Box
                  opacity={isCollapsed ? "0" : "1"}
                  transition="all 0.24s ease"
                >
                  Monitoring
                </Box>
              </Flex>

              {!isCollapsed && (
                    <Badge colorScheme="error" variant="solid" >
                      26
                    </Badge>
                  )}
            </Link>
          </Tooltip>
          
          <Tooltip
            label="Reporting"
            isDisabled={!isCollapsed}
            placement="right"
          >
            <Link
              as={NavLink}
              to={`/reporting/overview`}
              pr={isCollapsed ? 0 : 8}
              className="navLink"
            >
              {isCollapsed && (
                    <Box
                      as="span"
                      justifyContent="center"
                      opacity={isCollapsed ? 1 : 0}
                      bg={"error.500"}
                      transitionDelay="1s"
                      border="2px solid #fff"
                      borderRadius="full"
                      height="9px"
                      width="9px"
                      position="absolute"
                      right="12px"
                      top="12px"
                    
                      z-index="1"></Box>
                )}
              <Flex direction="row" alignItems="center" gap={2}>
                <LineChart size={16} />
                <Box
                  opacity={isCollapsed ? "0" : "1"}
                  transition="all 0.24s ease"
                >
                  Reporting
                </Box>
              </Flex>
              {!isCollapsed && (
                    <Badge colorScheme="error" variant="solid" >
                      8
                    </Badge>
                  )}
            </Link>
          </Tooltip>
          
        </Flex>
      </Flex>

      <Flex
        direction={"column"}
        justifyContent="flex-start"
        w="100%"
        className="navLinks"
        gap={1}
      >
        <Tooltip
          label="Administration"
          isDisabled={!isCollapsed}
          placement="right"
        >
          <Link
            to={`/administration`}
            as={NavLink}
            className="navLink"
            pr={isCollapsed ? 0 : 8}
          >
            <Flex direction="row" alignItems="center" gap={2}>
              <Settings size={16} />
              <Box
                opacity={isCollapsed ? "0" : "1"}
                transition="all 0.24s ease"
              >
                Administration
              </Box>
            </Flex>
          </Link>
        </Tooltip>

        <Tooltip label="Logout" isDisabled={!isCollapsed} placement="right">
          <Link
            textDecoration={"none"}
            _hover={{ textDecoration: "none" }}
            onClick={handleLogout}
            className="navLink"
            pr={isCollapsed ? 0 : 8}
          >
            <Flex direction="row" alignItems="center" gap={2}>
              <Power size={16} />
              <Box
                opacity={isCollapsed ? "0" : "1"}
                transition="all 0.24s ease"
              >
                Logout
              </Box>
            </Flex>
          </Link>
        </Tooltip>
      </Flex>
    </Flex>
  );
};

export default Navigation;
